import {
  Component,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
} from "@angular/core";

import { BrandLogoComponent } from "../brand-logo/brand-logo.component";

/**
 * The refusal card from board 7f ("Error: no access"): logo, eyebrow, title,
 * the sentence naming who was refused, a danger-toned advisory, one action,
 * the help row and the monospace incident code.
 *
 * One component because /forbidden and /no-access drew it by hand, twice,
 * with byte-identical styles — and a third refusal would have copied it again.
 * Everything that differs between them is an input; the sentence that names
 * the refused account is projected, because it carries its own bold markup
 * and its own "no email" fallback.
 *
 * Drops into app-auth-layout's form column. The host is `display: contents`
 * so each piece is a direct flex item of that column, as the hand-drawn
 * versions were.
 */
@Component({
  selector: "app-access-denied",
  standalone: true,
  imports: [BrandLogoComponent],
  templateUrl: "./access-denied.component.html",
  styleUrl: "./access-denied.component.scss",
  host: { "[class.denied--featured]": "featured()" },
})
export class AccessDeniedComponent {

  public readonly logoUrl: InputSignal<string> = input.required<string>();

  public readonly logoAlt: InputSignal<string> = input.required<string>();

  /** The small line above the title, e.g. "Starbucks · Davaco Customer Portal". */
  public readonly eyebrow: InputSignal<string> = input.required<string>();

  public readonly heading: InputSignal<string> = input.required<string>();

  /** What to do about it. Rendered in the danger-toned block. */
  public readonly advisory: InputSignal<string> = input.required<string>();

  public readonly actionLabel: InputSignal<string> = input.required<string>();

  /**
   * `admin_role_required · ref 8f2c-11` — the line support asks for.
   *
   * Optional: empty omits the line. The customer-host /no-access screen drops
   * it — a customer's fix is being added by their admin or account manager, not a
   * support ticket, and an internal error code read as noise on a branded
   * screen.
   */
  public readonly code: InputSignal<string> = input<string>("");

  /**
   * The customer's logo as the centrepiece — larger, centred, with a pulsing
   * accent ring — and the card's lines revealed in a short stagger.
   *
   * Off by default so /forbidden and the apex /no-access keep board 7f's quiet
   * layout; on for the customer-host /no-access, where the visitor is looking at
   * that customer's address and the brand should lead. All of the motion stops
   * under `prefers-reduced-motion`.
   */
  public readonly featured: InputSignal<boolean> = input<boolean>(false);

  public readonly action: OutputEmitterRef<void> = output<void>();
}
